/**
 * Typed API client.
 *
 * Two behaviours here are load-bearing rather than incidental:
 *
 *  1. Access tokens live 15 minutes, so a 45-minute exam WILL outlive one. On a
 *     401 the client rotates its refresh token once and replays the request, so
 *     a student never sees a login screen mid-paper.
 *
 *  2. Exactly one refresh may be in flight — see `refresh.ts`, which owns that
 *     promise and shares it with the boot-time restore. Refresh tokens rotate
 *     and the server treats a reused token as a leak; a burst of parallel 401s
 *     each rotating independently would trip that detector and sign the user
 *     out.
 */
import { getSession } from './session';
import { refreshOnce, RefreshFailed } from './refresh';

export class ApiError extends Error {
  constructor(
    readonly code: string, message: string, readonly status: number, readonly details?: unknown,
    /**
     * The request path of the value the server refused, when it was one value
     * — `password`, `self_enrol.parent.email`. A form reads it to put the
     * sentence under that box instead of at the top of the page; see
     * `lib/field-errors.ts`. Absent when the refusal is about the whole request.
     */
    readonly field?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

const BASE = '/api/v1';

/**
 * The single-flight refresh lives in `refresh.ts` and is shared with the
 * boot-time restore, which used to run its own `fetch` to the same endpoint —
 * two refreshes of one cookie, which is the race the promise was there to
 * prevent. The failure is re-thrown as an ApiError so every caller here still
 * catches one kind of error.
 */
async function refreshSession(): Promise<void> {
  try {
    await refreshOnce();
  } catch (err) {
    if (err instanceof RefreshFailed) {
      throw new ApiError('unauthorized', err.message, 401);
    }
    throw err;
  }
}

export interface RequestOptions {
  // PATCH for edits that change part of a thing — rewording a feedback
  // question, or retiring it — where POST would read as creating another.
  // PUT where the request REPLACES a whole set, as choosing which questions a
  // paper asks does: the body is the new state, not a change to it.
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** A FormData body is sent as multipart — used for question-paper uploads. */
  body?: unknown;
  headers?: Record<string, string>;
  /** Skip the Authorization header — for login, register, refresh. */
  anonymous?: boolean;
  signal?: AbortSignal;
  /** Let the request outlive the page, for a log written on the way out. */
  keepalive?: boolean;
}

export async function api<T = unknown>(
  path: string,
  { method = 'GET', body, headers = {}, anonymous = false, signal, keepalive }: RequestOptions = {},
  _retried = false,
): Promise<T> {
  const token = anonymous ? null : getSession()?.accessToken;
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: {
        // The browser must set content-type itself for multipart, because only
        // it knows the boundary string it generated.
        ...(isForm ? {} : { 'content-type': 'application/json' }),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...headers,
      },
      body: body === undefined ? undefined : (isForm ? (body as FormData) : JSON.stringify(body)),
      signal,
      keepalive,
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    // fetch only rejects when the request never reached a server. Saying so
    // beats "Failed to fetch", which reads like a rejected password.
    throw new ApiError(
      'network_unreachable',
      'Cannot reach the server. Check your connection and try again.',
      0,
    );
  }

  // Retried whenever we thought we were signed in. The refresh cookie is
  // invisible to this code, so 'do we have one' can only be answered by
  // asking the server.
  if (res.status === 401 && !anonymous && !_retried && getSession()) {
    await refreshSession();
    return api<T>(path, { method, body, headers, anonymous, signal, keepalive }, true);
  }

  const text = await res.text();
  const data = text ? JSON.parse(text) : {};

  if (!res.ok) {
    throw new ApiError(
      data.error ?? 'error',
      data.message ?? `${res.status} ${path}`,
      res.status,
      data.details,
      typeof data.field === 'string' ? data.field : undefined,
    );
  }
  return data as T;
}

// -------------------------------------------------------- shared API types
// Mirrors the TypeBox response schemas in apps/api/src/http/schemas.ts. When the
// API and web packages share a workspace type package these come from there
// directly; until then they are kept deliberately close to the schema names.

export interface AuthUser {
  sub: number;
  // 'learner' is a self-enrolled older student: the payer AND the student, one
  // account. See apps/api migration 015.
  role: 'owner' | 'finance' | 'academic' | 'reviewer' | 'support'
    | 'parent' | 'student' | 'learner';
  name: string;
  parentId?: number;
  studentId?: number;
  emailVerified: boolean;
  mobileVerified: boolean;
}

export interface Session {
  accessToken: string;
  /** Present only for non-browser clients that ask for `x-auth-mode: token`. */
  refreshToken?: string;
  expiresIn: number;
  user: AuthUser;
}

export interface Child {
  id: number;
  full_name: string;
  class_level: number;
  section: string | null;
  school: string | null;
  is_active: boolean;
  login_email: string | null;
}

export interface SubscriptionSummary {
  id: number;
  state: 'trial' | 'active' | 'grace' | 'past_due' | 'suspended' | 'paused' | 'cancelled' | 'expired';
  period_start: string;
  period_end: string;
  can_sit_test: boolean;
  state_description: string;
  plan_name: string;
  plan_code: string;
  price_paisa: string | number;
  billing_cycle: string;
  vat_rate_bp: number;
  /** What this subscription's trial was opened with. Zero if it never had one. */
  free_tests_granted: number;
  students: {
    id: number; full_name: string; class_level: number;
    /**
     * Free tests this child has left, or null when the subscription is not on
     * a trial. "None left" and "not on a trial" must not render the same — a
     * paying family shown "0 free tests left" reads it as something taken away.
     */
    free_tests_left: number | null;
  }[];
  events: { from_state: string | null; to_state: string; reason: string; created_at: string }[];
}

export interface Paper {
  id: number;
  title: string;
  class_level: number;
  window_start: string;
  window_end: string;
  duration_seconds: number;
  status: string;
  navigation_mode: 'free' | 'linear';
  results_published_at: string | null;
  attempt_id: number | null;
  attempt_state: string | null;
  eligibility: { allowed: boolean; reason: string; message: string };
}

export interface QuestionView {
  ordinal: number;
  questionId: number;
  stem: string;
  imageUrl: string | null;
  marks: number;
  /** Section heading on a sectioned sitting; null on a flat paper. */
  subjectName: string | null;
  options: { optionId: number; label: string; imageUrl: string | null }[];
  selectedOptionId: number | null;
  markedForReview: boolean;
  clientSeq: number;
}

export interface AttemptState {
  attemptId: number;
  state: string;
  navigationMode: 'free' | 'linear';
  junior: boolean;
  totalQuestions: number;
  currentOrdinal: number;
  remainingSeconds: number;
  serverNow: string;
  serverDeadline: string;
  questions: QuestionView[];
}

export interface StartAttempt {
  attemptId: number;
  sessionToken: string;
  resumed: boolean;
  serverDeadline: string;
  serverNow: string;
}

export interface AdvanceResult {
  advanced: boolean;
  atEnd: boolean;
  currentOrdinal: number;
  totalQuestions: number;
  question?: QuestionView;
}

export interface Scorecard {
  attemptId: number;
  paperTitle: string;
  studentName: string;
  submittedAt: string | null;
  autoSubmitted: boolean;
  score: number;
  maxScore: number;
  correct: number;
  wrong: number;
  unanswered: number;
  percentile: number | null;
  rankBand: string | null;
  breakdown: {
    bySubject: Record<string, { correct: number; wrong: number; unanswered: number; score: number; max: number }>;
    byChapter: Record<string, { correct: number; wrong: number; unanswered: number; score: number; max: number }>;
  };
  publishedAt: string | null;
}

export interface TrialBalance {
  accounts: { account: string; debits: number; credits: number; balance: number }[];
  totalDebits: number;
  totalCredits: number;
  balanced: boolean;
}
